import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  GridComponent, ColumnsDirective, ColumnDirective,
  Inject, Filter, Sort, Toolbar, Page, DetailRow,
} from '@syncfusion/ej2-react-grids';
import { DateRangePickerComponent } from '@syncfusion/ej2-react-calendars';
import { DialogComponent } from '@syncfusion/ej2-react-popups';

import * as XLSX from 'xlsx';
import Editor from '@monaco-editor/react';
import Loading from '../../components/customdesign/Loading';
// FlowStudioViewer (visor read-only nuevo, React Flow) — lazy para no
// sumar la dependencia al bundle principal de SearchWF.
const FlowStudioViewer = React.lazy(() => import('../admin/FlowStudio/FlowStudioViewer'));
import ApiClient from '../../api/ApiClient';
import EndPoint from '../../api/Endpoints';
import { prepareGetHistoryFlows, prepareGetFlowVars, getFlowFullVars, reactivateTask, killTask, isAdminUser } from '../../utils/Functions';
import * as ProcessActions from '../../redux/actions/process.actions';

// ─── module-level strings ref for cell templates ──────────────────────────────
// Set by SearchWF on mount; templates run outside React so they read this ref.
let _L = {};

// ─── constants ───────────────────────────────────────────────────────────────

const STATUS_CFG = {
  active:    { labelKey: 'swfInProgress', label: 'In Progress', dot: '#27ae60', bg: '#eafaf1', fg: '#1a7a45' },
  completed: { labelKey: 'swfCompleted',  label: 'Completed',   dot: '#2980b9', bg: '#eaf2fb', fg: '#1a5c8f' },
  error:     { labelKey: 'swfError',      label: 'Error',       dot: '#e74c3c', bg: '#fdecea', fg: '#a93226' },
};

// WFP_TMP_TASKS.STATUSID — mismos códigos documentados en docs/reactivate-task.md,
// docs/suspend-resume-task.md y docs/deactivate-task.md.
const TASK_STATUS_CFG = {
  4: { labelKey: 'swfTaskStatusPending',   label: 'Pending',    dot: '#2980b9', bg: '#eaf2fb', fg: '#1a5c8f' },
  // STATUSID=5: se marca en engine.model.js tras agotar reintentos (TRYOUT>=5) → error real, no completado.
  5: { labelKey: 'swfTaskStatusError',     label: 'Error',      dot: '#e74c3c', bg: '#fdecea', fg: '#a93226' },
  6: { labelKey: 'swfTaskStatusAuto',      label: 'Automatic',  dot: '#8e44ad', bg: '#f5eefa', fg: '#6c3483' },
  7: { labelKey: 'swfTaskStatusFinished',  label: 'Finished',   dot: '#27ae60', bg: '#eafaf1', fg: '#1a7a45' },
  8: { labelKey: 'swfTaskStatusRunning',   label: 'Running',    dot: '#f39c12', bg: '#fef6e7', fg: '#a5680a' },
  // Gris/slate (no rojo): "suspendida" es una pausa, no un error — antes
  // compartia la familia de rojo con STATUSID=5 (error) y se confundian.
  9: { labelKey: 'swfTaskStatusSuspended', label: 'Suspended',  dot: '#64748b', bg: '#eef1f5', fg: '#475569' },
};

function taskStatusCfg(statusid) {
  return TASK_STATUS_CFG[statusid] || { labelKey: null, label: 'Status ' + statusid, dot: '#95a5a6', bg: '#f1f2f3', fg: '#5c6266' };
}

// days: 0 usa labelKey (swfToday); el resto arma el texto en render con
// L.daysUnit para que "7/30/90 days" tambien se traduzca (antes quedaba
// fijo en ingles sin importar el idioma elegido).
const DATE_PRESETS_BASE = [
  { labelKey: 'swfToday', days: 0  },
  { labelKey: null,       days: 7  },
  { labelKey: null,       days: 30 },
  { labelKey: null,       days: 90 },
];

// VARNAME es estable entre idiomas (se usa para matchear con las variables
// del flujo); el VARLABEL visible se arma por idioma con getFixedCols(L).
const FIXED_VARNAMES = new Set([
  'WFPFLOWID', 'flowname', 'version', 'office',
  'startDate', 'endDate', 'durationLabel', 'flowStatus',
]);

function getFixedCols(L) {
  return [
    { VARNAME: 'WFPFLOWID',     VARLABEL: L.colInstanceId },
    { VARNAME: 'flowname',      VARLABEL: L.colFlow },
    { VARNAME: 'version',       VARLABEL: L.colVersion },
    { VARNAME: 'office',        VARLABEL: L.summaryOffice },
    { VARNAME: 'startDate',     VARLABEL: L.colStarted },
    { VARNAME: 'endDate',       VARLABEL: L.colEnded },
    { VARNAME: 'durationLabel', VARLABEL: L.summaryDuration },
    { VARNAME: 'flowStatus',    VARLABEL: L.statusLabel },
  ];
}

const CELL_MAX = 65;

// ─── helpers ─────────────────────────────────────────────────────────────────

function getRange(days) {
  const to = new Date(); to.setHours(23, 59, 59, 999);
  const from = new Date(); from.setDate(from.getDate() - days); from.setHours(0, 0, 0, 0);
  return { from, to };
}

function fmtShort(d) {
  if (!d) return '';
  return new Intl.DateTimeFormat('es', { day: '2-digit', month: 'short', year: '2-digit' }).format(d);
}

function fmtDateTime(d) {
  if (!d || d === '-') return '—';
  try {
    // timeZone: 'UTC' — ver nota en formatDateTimeFull: el backend serializa
    // estas fechas con sufijo "Z" pero los dígitos ya son la hora local de
    // Bolivia. Sin esto, el navegador reaplica su propio huso horario y la
    // fecha mostrada queda corrida (p.ej. -5h si el navegador está en UTC-5).
    return new Intl.DateTimeFormat('es', {
      day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
      timeZone: 'UTC',
    }).format(new Date(d));
  } catch { return String(d); }
}

// Detects date-like cell values (Date objects, ISO/MySQL datetime strings, or the
// "Wed Jun 24 2026 ..." shape produced when a Date got stringified upstream) so the
// grid can auto-format them without needing an explicit column type from the backend.
function isDateLikeValue(v) {
  if (v instanceof Date) return !isNaN(v.getTime());
  if (typeof v !== 'string' || !v) return false;
  return /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/.test(v) ||
         /^[A-Za-z]{3}\s[A-Za-z]{3}\s\d{2}\s\d{4}/.test(v);
}

function pad2(n) { return String(n).padStart(2, '0'); }

// dd/mm/yyyy hh24:mi:ss. Usa los getters UTC (no los locales) porque el
// backend serializa STARTDATE/ENDDATE con sufijo "Z" aunque los dígitos ya
// son la hora local de Bolivia (DB en UTC-4, Lambda en UTC, sin conversión
// real de por medio — ver CLAUDE.md "Cadena horaria de facturación"). Leerlos
// con getters locales le reaplica el huso horario del navegador y corre la
// hora mostrada (reportado: -5h).
function formatDateTimeFull(v) {
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return typeof v === 'string' ? v : '—';
  return `${pad2(d.getUTCDate())}/${pad2(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}`;
}

// Generic Syncfusion valueAccessor: reformats the cell only if its raw value looks
// like a date, leaving every other column (text/number/status) untouched. Works for
// fixed columns (startDate/endDate) and dynamic process-variable date columns alike.
function dateAwareValueAccessor(field, data) {
  const v = data ? data[field] : undefined;
  if (v == null || v === '') return v ?? '';
  return isDateLikeValue(v) ? formatDateTimeFull(v) : v;
}

function exportCSV(flows, header) {
  if (!flows.length) return;
  const cols = header.map(h => h.VARLABEL).join(',');
  const rows = flows.map(r => header.map(h => `"${r[h.VARNAME] ?? ''}"`).join(','));
  const blob = new Blob([[cols, ...rows].join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'instances.csv'; a.click();
  URL.revokeObjectURL(url);
}

function exportExcel(flows, header) {
  if (!flows.length) return;
  const rows = flows.map(r => {
    const obj = {};
    header.forEach(h => { obj[h.VARLABEL] = r[h.VARNAME] ?? ''; });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(rows);
  const colWidths = header.map(h => ({ wch: Math.max(h.VARLABEL.length, 12) }));
  ws['!cols'] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Instances');
  XLSX.writeFile(wb, 'instances.xlsx');
}

// Detecta si el valor de una variable de flujo es un JSON serializado (objeto o
// array) para poder mostrarlo formateado en vez de como una sola linea cruda.
function tryParseJSON(value) {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  if (!((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']')))) return null;
  try {
    const parsed = JSON.parse(s);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

// Largo maximo de un valor de variable antes de truncarlo con "Ver completo"
// en el panel de detalle — evita que un campo gigante (texto largo o JSON)
// ensucie la vista principal; el contenido completo se ve en un popup aparte.
const VAR_PREVIEW_LIMIT = 240;

function truncatePreview(text, limit = VAR_PREVIEW_LIMIT) {
  if (text.length <= limit) return { preview: text, truncated: false };
  return { preview: text.slice(0, limit) + '…', truncated: true };
}

function parseResponse(raw) {
  if (!raw) return [];
  return raw.split('|_|').filter(p => p.trim()).map(part => {
    try { return { type: 'json', data: JSON.parse(part.trim()) }; }
    catch { return { type: 'text', raw: part.trim() }; }
  });
}

// Visor JSON de solo lectura con Monaco (resaltado + plegado).
function JsonViewer({ data }) {
  const text = React.useMemo(() => {
    try { return JSON.stringify(data, null, 2); }
    catch { return String(data); }
  }, [data]);

  const lines = text.split('\n').length;
  const height = Math.min(420, Math.max(120, lines * 19 + 16));
  const isDark = useDarkMode();

  return (
    <div style={{ border: '1px solid var(--xflow-line, #d0d7de)', borderRadius: 4, overflow: 'hidden' }}>
      <Editor
        height={height}
        defaultLanguage="json"
        value={text}
        theme={isDark ? 'vs-dark' : 'light'}
        options={{
          readOnly: true,
          domReadOnly: true,
          fontSize: 12,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          lineNumbers: 'on',
          folding: true,
          automaticLayout: true,
          renderLineHighlight: 'none',
          scrollbar: { alwaysConsumeMouseWheel: false },
          contextmenu: false,
        }}
      />
    </div>
  );
}

// ─── FlowDetailPanel ─────────────────────────────────────────────────────────

function DetailSection({ title, badge, defaultOpen = true, T, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div style={{ borderBottom: `1px solid ${T.border}` }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '11px 20px', background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 11, fontWeight: 700, color: T.secondary,
          textTransform: 'uppercase', letterSpacing: '0.6px',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {title}
          {badge != null && (
            <span style={{
              fontSize: 10, fontWeight: 600, borderRadius: 10,
              padding: '1px 6px', lineHeight: '16px',
              background: T.badgeBg, color: T.secondary,
              border: `1px solid ${T.border}`,
            }}>{badge}</span>
          )}
        </span>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          {open
            ? <polyline points="18 15 12 9 6 15"/>
            : <polyline points="6 9 12 15 18 9"/>}
        </svg>
      </button>
      {open && <div style={{ padding: '4px 20px 16px' }}>{children}</div>}
    </div>
  );
}

function TaskTimelineItem({ task, isLast, T, dark, onReactivate, onKill, L }) {
  const [expandResp, setExpandResp] = React.useState(false);
  const resp = task.ADDCOMMENTARY || '';

  const sections = parseResponse(resp);
  const firstSec = sections[0];
  const statusOk  = firstSec?.data?.status === true  || firstSec?.data?.status === 'true';
  const statusErr = firstSec?.data?.status === false || firstSec?.data?.status === 'false';
  const dotColor  = statusOk ? '#27ae60' : statusErr ? '#e74c3c' : task.ENDDATE && task.ENDDATE !== '-' ? '#2980b9' : '#7a9bbf';

  return (
    <div style={{ display: 'flex', gap: 12, position: 'relative' }}>
      {!isLast && (
        <div style={{
          position: 'absolute', left: 7, top: 20, bottom: -8,
          width: 1, background: T.border,
        }} />
      )}
      <div style={{
        flexShrink: 0, width: 15, height: 15, borderRadius: '50%', marginTop: 3,
        background: dotColor, border: `2px solid ${T.panelBg}`,
        boxShadow: `0 0 0 2px ${dotColor}44`,
      }} />
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.primary, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          {task.TASK}
          {task.STATUSID != null && (() => {
            const cfg = taskStatusCfg(task.STATUSID);
            const label = (cfg.labelKey && L[cfg.labelKey]) || cfg.label;
            return (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: dark ? `${cfg.dot}22` : cfg.bg,
                color: dark ? cfg.dot : cfg.fg,
                border: `1px solid ${cfg.dot}33`,
                borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 600,
                whiteSpace: 'nowrap', letterSpacing: 0.2,
              }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                {label}
              </span>
            );
          })()}
          {task.TRYOUT > 1 && (
            <span style={{
              fontSize: 10, fontWeight: 600, color: dark ? '#f39c12' : '#a5680a',
              background: dark ? 'rgba(243,156,18,0.12)' : '#fef6e7',
              border: `1px solid ${dark ? 'rgba(243,156,18,0.3)' : '#fbe4b0'}`,
              borderRadius: 4, padding: '1px 7px', whiteSpace: 'nowrap',
            }}>
              {(L.taskAttempts || 'Attempts')}: {task.TRYOUT}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: T.secondary }}>{fmtDateTime(task.STARTDATE)}</span>
          {task.ENDDATE && task.ENDDATE !== '-' && (
            <span style={{ fontSize: 11, color: T.muted }}>→ {fmtDateTime(task.ENDDATE)}</span>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: resp ? 6 : 0 }}>
          {task.USERNAME && (
            <span style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 3 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
              {task.USERNAME}
            </span>
          )}
          {task.ACTOR && (
            <span style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 3 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>
              {task.ACTOR}
            </span>
          )}
          {task.OFFICE && (
            <span style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 3 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
              {task.OFFICE}
            </span>
          )}
        </div>
        {onReactivate && (task.STATUSID === 5 || (task.ENDDATE && task.ENDDATE !== '-')) && task.ID != null && (
          <button
            type="button"
            onClick={() => onReactivate(task)}
            title={L.reactivateTask}
            style={{
              fontSize: 11, fontWeight: 600,
              color: dark ? '#fbbf24' : '#b45309',
              background: 'rgba(245,158,11,0.12)',
              border: '1px solid rgba(245,158,11,0.4)',
              borderRadius: 6, padding: '3px 9px', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 6,
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/>
            </svg>
            {L.reactivateTask}
          </button>
        )}
        {onKill && task.STATUSID === 4 && task.ID != null && (
          <button
            type="button"
            onClick={() => onKill(task)}
            title={L.killTask}
            style={{
              fontSize: 11, fontWeight: 600,
              color: dark ? '#f87171' : '#b91c1c',
              background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.4)',
              borderRadius: 6, padding: '3px 9px', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 6, marginLeft: 6,
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9"/><line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/>
            </svg>
            {L.killTask}
          </button>
        )}
        {resp && (
          <div>
            <button
              type="button"
              onClick={() => setExpandResp(o => !o)}
              style={{
                fontSize: 11, color: T.blue, background: 'none', border: 'none',
                cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                {expandResp
                  ? <polyline points="18 15 12 9 6 15"/>
                  : <polyline points="6 9 12 15 18 9"/>}
              </svg>
              {expandResp ? L.hideResponse : L.viewResponse}
            </button>
            {expandResp && (
              <div style={{ marginTop: 6 }}>
                {sections.map((sec, si) => (
                  <div key={si} style={{ marginBottom: si < sections.length - 1 ? 8 : 0 }}>
                    {sections.length > 1 && (
                      <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 3 }}>
                        {L.response} {si + 1}
                      </div>
                    )}
                    <pre style={{
                      margin: 0, padding: '7px 10px',
                      background: T.card, border: `1px solid ${T.cardBorder}`,
                      borderRadius: 4, fontSize: 11, color: T.primary,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      maxHeight: 150, overflowY: 'auto',
                    }}>
                      {sec.type === 'json'
                        ? JSON.stringify(sec.data, null, 2)
                        : sec.raw}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FlowDetailPanel({ row, tasks, header, onClose, isDark: dark, isAdmin, onReactivate, onKill, L }) {
  const T = dark ? {
    panelBg: '#111a2e', hdrBg: '#0d1525', border: '#1e2d47',
    primary: '#c8d8f0', secondary: '#7a9bbf', muted: '#4a6180',
    card: '#1a2540', cardBorder: '#1e2d47', blue: '#38a4d8',
    chipBg: '#1a2540', badgeBg: '#1e2d47',
  } : {
    panelBg: '#ffffff', hdrBg: '#f8fafc', border: '#e5e7eb',
    primary: '#1d2129', secondary: '#6b7280', muted: '#9ca3af',
    card: '#f9fafb', cardBorder: '#e5e7eb', blue: '#0070f2',
    chipBg: '#f1f5f9', badgeBg: '#f3f4f6',
  };

  const cfg = STATUS_CFG[row.flowStatus] || STATUS_CFG.active;

  // Set inicial: lo que ya vino con la grilla (get-flow-instance-vars), limitado
  // a las variables definidas en el diseño del flujo — se muestra al toque, sin
  // esperar red. Para usuarios normales esto es todo lo que se muestra (a
  // proposito, es el recorte "de reporte"). Solo ADMIN dispara ademas el set
  // completo de la instancia (get-flow-full-vars, sin ese recorte) y cuando
  // llega reemplaza al inicial.
  const varCols = header.filter(h => !FIXED_VARNAMES.has(h.VARNAME));
  const initialVarEntries = varCols
    .map(h => ({ label: h.VARLABEL, value: row[h.VARNAME] }))
    .filter(e => e.value != null && String(e.value).trim() !== '' && String(e.value) !== '-');

  const [fullVarEntries, setFullVarEntries] = React.useState(null);
  const [loadingFullVars, setLoadingFullVars] = React.useState(isAdmin);

  React.useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setFullVarEntries(null);
    setLoadingFullVars(true);
    getFlowFullVars(row.WFPFLOWID).then(resp => {
      if (cancelled) return;
      if (resp.status) {
        const entries = (resp.result || [])
          .map(v => ({ label: v.VARLABEL || v.VARNAME, value: v.VARVALUE }))
          .filter(e => e.value != null && String(e.value).trim() !== '' && String(e.value) !== '-');
        setFullVarEntries(entries);
      }
      setLoadingFullVars(false);
    });
    return () => { cancelled = true; };
  }, [row.WFPFLOWID, isAdmin]);

  const varEntries = fullVarEntries ?? initialVarEntries;

  // Valor de variable abierto en popup (cuando se truncó por largo) — { label, json, text }.
  const [expandedVar, setExpandedVar] = React.useState(null);

  const flowTasks = tasks
    .filter(t => String(t.WFPFLOWID) === String(row.WFPFLOWID))
    .sort((a, b) => new Date(a.STARTDATE) - new Date(b.STARTDATE));

  const summaryItems = [
    { label: L.summaryStart,    value: fmtDateTime(row.startDate) },
    { label: L.summaryEnd,      value: fmtDateTime(row.endDate)   },
    { label: L.summaryDuration, value: row.durationLabel || '—'   },
    { label: L.summaryOffice,   value: row.office || '—'          },
    row.version ? { label: L.summaryVersion, value: `v${row.version}` } : null,
  ].filter(Boolean);

  const panelW = Math.min(560, window.innerWidth - 12);

  return (
    <div className="swf-detail-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="swf-detail-panel" style={{ width: panelW, background: T.panelBg }}>

        {/* ── Header ── */}
        <div style={{
          background: T.hdrBg, borderBottom: `1px solid ${T.border}`,
          padding: '14px 20px', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: T.muted, fontVariantNumeric: 'tabular-nums' }}>
                  #{row.WFPFLOWID}
                </span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: dark ? `${cfg.dot}22` : cfg.bg,
                  color: dark ? cfg.dot : cfg.fg,
                  border: `1px solid ${cfg.dot}33`,
                  borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                  {L[cfg.labelKey] || cfg.label}
                </span>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.primary, lineHeight: 1.3 }}>
                {row.flowname}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
                color: T.secondary, padding: 4, borderRadius: 4,
                display: 'flex', alignItems: 'center',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* summary chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {summaryItems.map((item, i) => (
              <span key={i} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: T.chipBg, border: `1px solid ${T.border}`,
                borderRadius: 4, padding: '3px 9px', fontSize: 11,
              }}>
                <span style={{ color: T.muted, fontWeight: 500 }}>{item.label}:</span>
                <span style={{ color: T.primary, fontWeight: 600 }}>{item.value}</span>
              </span>
            ))}
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex: 1, overflowY: 'auto' }}>

          {/* Variables del proceso */}
          <DetailSection title={L.dataSection} badge={varEntries.length} T={T}>
            {varEntries.length === 0 && !loadingFullVars ? (
              <p style={{ color: T.muted, fontSize: 13, margin: 0 }}>{L.noVars}</p>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))',
                gap: 8,
              }}>
                {varEntries.map((e, i) => {
                  const json = isAdmin ? tryParseJSON(e.value) : null;
                  const fullText = json ? JSON.stringify(json, null, 2) : String(e.value);
                  const { preview, truncated } = truncatePreview(fullText);
                  return (
                    <div key={i} style={{
                      background: T.card, border: `1px solid ${T.cardBorder}`,
                      borderRadius: 6, padding: '8px 12px',
                      gridColumn: json ? '1 / -1' : undefined,
                    }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: T.muted,
                        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3,
                      }}>
                        {e.label}
                      </div>
                      {json ? (
                        <pre style={{
                          margin: 0, fontSize: 12, lineHeight: 1.5,
                          color: dark ? '#a9c4e8' : '#1f2937',
                          background: dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
                          border: `1px solid ${dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
                          borderRadius: 4, padding: '6px 8px',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          fontFamily: 'Consolas, Menlo, monospace',
                          colorScheme: dark ? 'dark' : 'light',
                        }}>
                          {preview}
                        </pre>
                      ) : (
                        <div style={{ fontSize: 13, color: T.primary, wordBreak: 'break-word', fontWeight: 500 }}>
                          {preview}
                        </div>
                      )}
                      {truncated && (
                        <button
                          type="button"
                          onClick={() => setExpandedVar({ label: e.label, json, text: fullText })}
                          style={{
                            marginTop: 5, fontSize: 11, fontWeight: 600, color: T.blue,
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                          }}
                        >
                          {L.viewFullValue}
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
                            <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
                {loadingFullVars && (
                  <div style={{
                    background: T.card, border: `1px solid ${T.cardBorder}`,
                    borderRadius: 6, padding: '8px 12px',
                  }}>
                    <div style={{
                      animation: 'xmt-pulse 1.4s ease-in-out infinite',
                      background: T.badgeBg, borderRadius: 4, height: 10, width: '70%', marginBottom: 8,
                    }} />
                    <div style={{
                      animation: 'xmt-pulse 1.4s ease-in-out infinite 0.15s',
                      background: T.badgeBg, borderRadius: 4, height: 14, width: '90%', marginBottom: 4,
                    }} />
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                      {L.loadingRemainingVars}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DetailSection>

          {/* Historial de tareas */}
          <DetailSection title={L.taskHistory} badge={flowTasks.length} T={T}>
            {flowTasks.length === 0 ? (
              <p style={{ color: T.muted, fontSize: 13, margin: 0 }}>{L.noTasks}</p>
            ) : (
              <div className="swf-timeline">
                {flowTasks.map((t, i) => (
                  <TaskTimelineItem
                    key={i} task={t}
                    isLast={i === flowTasks.length - 1}
                    T={T}
                    dark={dark}
                    onReactivate={onReactivate}
                    onKill={onKill}
                    L={L}
                  />
                ))}
              </div>
            )}
          </DetailSection>

        </div>
      </div>

      {/* Valor de variable completo (cuando se truncó por largo) */}
      {expandedVar && (
        <DialogComponent header={expandedVar.label} width="640px"
          cssClass="swf-response-dlg"
          isModal={true} showCloseIcon={true} visible={true}
          close={() => setExpandedVar(null)} overlayClick={() => setExpandedVar(null)}>
          <div style={{ maxHeight: 480, overflowY: 'auto', padding: '2px 4px' }}>
            {expandedVar.json
              ? <JsonViewer data={expandedVar.json} />
              : (
                <p style={{
                  background: dark ? '#1a2540' : '#f6f8fa',
                  border: `1px solid ${T.cardBorder}`, borderRadius: 4,
                  padding: '10px 14px', margin: 0, fontSize: 13, color: T.primary,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {expandedVar.text}
                </p>
              )}
          </div>
        </DialogComponent>
      )}
    </div>
  );
}

// ─── module-level callbacks ──────────────────────────────────────────────────
let _openDetail     = null;
let _openFlowDetail = null;

// ─── cell templates (defined outside component — Syncfusion requirement) ─────

function StatusTemplate(row) {
  const dark = document.documentElement.getAttribute('data-theme') === 'dark';
  const cfg = STATUS_CFG[row.flowStatus] || STATUS_CFG.active;
  const label = _L[cfg.labelKey] || cfg.label;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: dark ? `${cfg.dot}22` : cfg.bg,
      color: dark ? cfg.dot : cfg.fg,
      border: `1px solid ${cfg.dot}33`,
      borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
      whiteSpace: 'nowrap', letterSpacing: 0.2,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {label}
    </span>
  );
}

function DurationTemplate(row) {
  const dark = document.documentElement.getAttribute('data-theme') === 'dark';
  return (
    <span style={{ fontSize: 12, color: dark ? '#7a9bbf' : '#555', fontVariantNumeric: 'tabular-nums' }}>
      {row.durationLabel || '—'}
    </span>
  );
}

function TaskStatusTemplate(row) {
  const dark = document.documentElement.getAttribute('data-theme') === 'dark';
  const cfg = taskStatusCfg(row.STATUSID);
  const label = (cfg.labelKey && _L[cfg.labelKey]) || cfg.label;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: dark ? `${cfg.dot}22` : cfg.bg,
      color: dark ? cfg.dot : cfg.fg,
      border: `1px solid ${cfg.dot}33`,
      borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
      whiteSpace: 'nowrap', letterSpacing: 0.2,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {label}
    </span>
  );
}

function TaskAttemptsTemplate(row) {
  const dark = document.documentElement.getAttribute('data-theme') === 'dark';
  const n = row.TRYOUT ?? 0;
  return (
    <span style={{
      fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
      color: n > 1 ? (dark ? '#f39c12' : '#a5680a') : (dark ? '#7a9bbf' : '#555'),
    }}>
      {n}
    </span>
  );
}

function DetailButtonTemplate(row) {
  const dark = document.documentElement.getAttribute('data-theme') === 'dark';
  const hasError = row.flowStatus === 'error';
  return (
    <button
      type="button"
      title={hasError ? (_L.hasErrorHint || 'Esta instancia tiene una tarea en error — click para expandir') : (_L.viewData || 'Ver datos')}
      onClick={e => { e.stopPropagation(); _openFlowDetail?.(row); }}
      style={{
        position: 'relative',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '4px 7px', borderRadius: 4, cursor: 'pointer',
        background: hasError ? (dark ? 'rgba(231,76,60,0.14)' : '#fdecea') : (dark ? 'rgba(56,164,216,0.12)' : '#e8f4fd'),
        color: hasError ? (dark ? '#e74c3c' : '#a93226') : (dark ? '#38a4d8' : '#1a6fa8'),
        border: `1px solid ${hasError ? (dark ? 'rgba(231,76,60,0.45)' : '#f3b8b2') : (dark ? 'rgba(56,164,216,0.3)' : '#b3d9f5')}`,
        transition: 'background 0.15s',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
      {hasError && (
        <span className="xswf-error-ping" style={{
          position: 'absolute', top: -3, right: -3, width: 9, height: 9, borderRadius: '50%',
          background: '#e74c3c', border: `1.5px solid ${dark ? '#0e1626' : '#fff'}`,
        }} />
      )}
    </button>
  );
}

// Extract a human-readable summary from a single parsed section
function sectionSummary(sec) {
  if (sec.type === 'text') return sec.raw.slice(0, 60);
  const d = sec.data;
  if (!d || typeof d !== 'object') return JSON.stringify(d).slice(0, 60);
  const PRIORITY = ['message', 'msg', 'error', 'description', 'result', 'status'];
  for (const key of PRIORITY) {
    const val = d[key];
    if (val !== undefined && val !== null && typeof val !== 'object') {
      const statusIcon = key === 'status' ? (val ? '✓' : '✗') : null;
      return statusIcon ? `${statusIcon} ${key}: ${val}` : `${key}: ${String(val).slice(0, 50)}`;
    }
  }
  const firstKey = Object.keys(d).find(k => typeof d[k] !== 'object' && d[k] !== null);
  if (firstKey) return `${firstKey}: ${String(d[firstKey]).slice(0, 50)}`;
  return '{…}';
}

function ResponseTemplate(row) {
  const dark = document.documentElement.getAttribute('data-theme') === 'dark';
  const text = row.ADDCOMMENTARY || '';
  if (!text) return <span style={{ fontSize: 12, color: dark ? '#3a5070' : '#aaa' }}>—</span>;

  const sections = parseResponse(text);
  const hasMore  = sections.length > 0;

  const preview  = sections.length > 0 ? sectionSummary(sections[0]) : text.slice(0, 60);
  const isOk     = sections[0]?.data?.status === true  || sections[0]?.data?.status === 'true';
  const isErr    = sections[0]?.data?.status === false || sections[0]?.data?.status === 'false';

  if (sections.length === 1 && sections[0].type === 'text' && sections[0].raw.length <= CELL_MAX) {
    return <span style={{ fontSize: 12, color: dark ? '#a0bde0' : '#444' }}>{sections[0].raw}</span>;
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>
      {(isOk || isErr) && (
        <span style={{
          flexShrink: 0, width: 7, height: 7, borderRadius: '50%',
          background: isOk ? '#27ae60' : '#e74c3c',
        }} />
      )}
      <span style={{
        flex: 1, minWidth: 0, fontSize: 12,
        color: dark ? '#a0bde0' : '#374151',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {preview}
      </span>
      {sections.length > 1 && (
        <span style={{
          flexShrink: 0, fontSize: 10, fontWeight: 600,
          color: dark ? '#7a9bbf' : '#6b7280',
          background: dark ? '#1e2d47' : '#f3f4f6',
          border: `1px solid ${dark ? '#2d4168' : '#e5e7eb'}`, borderRadius: 10,
          padding: '0px 5px', lineHeight: '16px',
        }}>
          +{sections.length}
        </span>
      )}
      {hasMore && (
        <button
          onClick={() => _openDetail?.(text)}
          style={{
            flexShrink: 0, height: 20, padding: '0 7px',
            borderRadius: 3, border: `1px solid ${dark ? '#2d4168' : '#e5e7eb'}`,
            background: dark ? '#1e2d47' : '#f9fafb', cursor: 'pointer',
            fontSize: 11, color: dark ? '#38a4d8' : '#2563eb', fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 3, lineHeight: 1,
          }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
            <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
          </svg>
          {_L.viewResponse || 'view'}
        </button>
      )}
    </span>
  );
}

// ─── hook: reacciona al cambio de data-theme en <html> ───────────────────────

function useDarkMode() {
  const [dark, setDark] = React.useState(
    () => document.documentElement.getAttribute('data-theme') === 'dark'
  );
  React.useEffect(() => {
    const obs = new MutationObserver(() =>
      setDark(document.documentElement.getAttribute('data-theme') === 'dark')
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

// ─── component ───────────────────────────────────────────────────────────────

let gridRef;

const SearchWF = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { defConfig, defLang } = useSelector(s => s.Login);

  // Localized strings — one place, all with fallbacks
  const L = React.useMemo(() => {
    const c = defConfig?.[defLang] || {};
    return {
      title:              c.swfTitle              || 'Flow Instance Search',
      resultsTitle:       c.swfResultsTitle       || 'Flow Instances',
      datePeriod:         c.swfDatePeriod         || 'Date period',
      processLabel:       c.swfProcessLabel       || 'Flow *',
      statusLabel:        c.swfStatusLabel        || 'Status',
      officeLabel:        c.swfOfficeLabel        || 'Office / Code',
      searchBtn:          c.swfSearchBtn          || 'Search',
      selectProcess:      c.swfSelectProcess      || '— Select flow —',
      allStatuses:        c.swfAllStatuses        || 'All statuses',
      swfInProgress:      c.swfInProgress         || 'In Progress',
      swfCompleted:       c.swfCompleted          || 'Completed',
      swfError:           c.swfError              || 'Error',
      swfToday:           c.swfToday              || 'Today',
      officePH:           c.swfOfficePlaceholder  || 'Search office…',
      customRange:        c.swfCustomRange        || 'Custom range…',
      avgDuration:        c.swfAvgDuration        || 'Avg. duration',
      noInstances:        c.swfNoInstances        || 'No instances found',
      noInstancesHint:    c.swfNoInstancesHint    || 'Try expanding the date range or removing filters',
      responseDetail:     c.swfResponseDetail     || 'Response detail',
      viewResponse:       c.swfViewResponse       || 'View response',
      hideResponse:       c.swfHideResponse       || 'Hide response',
      response:           c.swfResponse           || 'Response',
      reactivateTask:     c.swfReactivateTask     || 'Reactivate task',
      taskReactivated:    c.swfTaskReactivated    || 'Task reactivated',
      taskReactivateError:c.swfTaskReactivateError|| 'Could not reactivate task',
      killTask:           c.swfKillTask           || 'Deactivate task',
      taskKilled:         c.swfTaskKilled         || 'Task deactivated',
      taskKillError:      c.swfTaskKillError      || 'Could not deactivate task',
      taskStatus:         c.swfTaskStatus         || 'Status',
      taskAttempts:       c.swfTaskAttempts       || 'Attempts',
      swfTaskStatusPending:   c.swfTaskStatusPending   || 'Pending',
      swfTaskStatusCompleted: c.swfTaskStatusCompleted || 'Completed',
      swfTaskStatusAuto:      c.swfTaskStatusAuto      || 'Automatic',
      swfTaskStatusFinished:  c.swfTaskStatusFinished  || 'Finished',
      swfTaskStatusRunning:   c.swfTaskStatusRunning   || 'Running',
      swfTaskStatusSuspended: c.swfTaskStatusSuspended || 'Suspended',
      selectRowDetails:   c.swfSelectRowForDetails|| 'Select a row to view details',
      selectRowDiagram:   c.swfSelectRowForDiagram|| 'Select one row to view diagram',
      refresh:            c.swfRefresh            || 'Refresh',
      viewData:           c.swfViewData           || 'Ver datos',
      diagram:            c.swfDiagram            || 'Diagrama',
      noVars:             c.swfNoVars             || 'No additional variables recorded.',
      loadingRemainingVars: c.swfLoadingRemainingVars || 'Cargando datos restantes…',
      viewFullValue:      c.swfViewFullValue       || 'Ver completo',
      noTasks:            c.swfNoTasks            || 'No tasks recorded.',
      dataSection:        c.swfDataSection        || 'Flow data',
      taskHistory:        c.swfTaskHistory        || 'Task history',
      summaryStart:       c.swfSummaryStart       || 'Start',
      summaryEnd:         c.swfSummaryEnd         || 'End',
      summaryDuration:    c.swfSummaryDuration    || 'Duration',
      summaryOffice:      c.swfSummaryOffice      || 'Office',
      summaryVersion:     c.swfSummaryVersion     || 'Version',
      selectProcessReq:   c.swfSelectProcessReq   || 'Select a flow',
      multiFlowHint:       c.swfMultiFlowHint       || 'Ctrl+Clic para elegir varios flujos',
      multiFlowCount:      c.swfMultiFlowCount      || 'seleccionados',
      multiFlowPrimaryHint:c.swfMultiFlowPrimaryHint|| 'El primer flujo define las columnas y el filtro de variables',
      showAllTasks:        c.swfShowAllTasks        || 'Ver todas las tareas',
      daysUnit:            c.swfDaysUnit            || 'días',
      pageLabel:           c.swfPageLabel           || 'Página',
      instanceIdLabel:     c.swfInstanceIdLabel     || 'ID Instancia',
      instanceIdPH:        c.swfInstanceIdPH        || 'Ej: 263',
      varFilterLabel:      c.swfVarFilterLabel      || 'Variable',
      varFilterAny:        c.swfVarFilterAny        || '— Variable —',
      varFilterValuePH:    c.swfVarFilterValuePH    || 'Valor contiene...',
      colInstanceId:       c.swfColInstanceId       || 'Instancia',
      colFlow:             c.swfColFlow             || 'Flujo',
      colVersion:          c.swfColVersion          || 'Ver.',
      colStarted:          c.swfColStarted          || 'Iniciado',
      colEnded:            c.swfColEnded            || 'Finalizado',
      colTask:             c.swfColTask             || 'Tarea',
      colUser:             c.swfColUser             || 'Usuario',
      colActor:            c.swfColActor            || 'Actor',
      hasErrorHint:        c.swfHasErrorHint        || 'Esta instancia tiene una tarea en error — click para expandir',
    };
  }, [defConfig, defLang]);

  // Keep module-level ref in sync for cell templates (outside React lifecycle)
  React.useEffect(() => { _L = L; }, [L]);

  const [flows,        setFlows]        = React.useState([]);
  const [flowVars,     setFlowVars]     = React.useState([]);
  const [header,       setHeader]       = React.useState([]);
  const [historyFlows, setHistoryFlows] = React.useState([]);
  const [historyTasks, setHistoryTasks] = React.useState([]);
  const [loading,      setLoading]      = React.useState(false);
  const [searched,     setSearched]     = React.useState(false);
  const [stats,        setStats]        = React.useState({ total: 0, active: 0, completed: 0, error: 0, avg: '' });

  // filters
  const [preset,    setPreset]    = React.useState(1);
  const [dateFrom,  setDateFrom]  = React.useState(() => getRange(7).from);
  const [dateTo,    setDateTo]    = React.useState(() => getRange(7).to);
  // Flujo(s) seleccionado(s) — select multiple. El PRIMERO de la lista es el
  // flujo "principal": solo sus variables definen las columnas del grid y el
  // filtro por variable. Los demas solo se agregan a la busqueda secuencial.
  const [selFlows, setSelFlows] = React.useState([]);
  const primaryFlow = selFlows.length ? selFlows[0] : '';
  const [selStatus, setSelStatus] = React.useState('all');
  // Filtro por instancia exacta (WFP_TMP_FLOWS.ID) — solo se setea via deep-link
  // desde el widget de errores del header; el buscador normal no lo expone.
  const [wfpflowidFilter, setWfpflowidFilter] = React.useState(null);
  const [noFill,    setNoFill]    = React.useState(true);
  const [officeQ,   setOfficeQ]   = React.useState('');

  // Filtro por variable de instancia (WFP_TMP_FLOWS_VARS via EXISTS en el back)
  const [varFilterName,  setVarFilterName]  = React.useState('');
  const [varFilterValue, setVarFilterValue] = React.useState('');

  // Paginacion server-side (paginas de 50 — pageSize lo fija el back)
  const [page,     setPage]     = React.useState(0);
  const [pageInfo, setPageInfo] = React.useState({ total: 0, pageSize: 50, hasMore: false });

  // diagram (visor único: FlowStudioViewer)
  const [diagProps, setDiagProps] = React.useState({ flowId: 0, id: 0, version: 0, tasks: [] });
  const [showStudioView, setShowStudioView] = React.useState(false);

  // cell detail
  const [detail, setDetail] = React.useState(null);

  // flow detail panel
  const [detailRow, setDetailRow] = React.useState(null);

  React.useEffect(() => {
    _openDetail     = (t)   => setDetail(t);
    _openFlowDetail = (row) => setDetailRow(row);
    return () => { _openDetail = null; _openFlowDetail = null; };
  }, []);

  // Fix: Syncfusion filter dialog overflow
  React.useEffect(() => {
    const MARGIN = 8;

    const fixFilterDialog = () => {
      setTimeout(() => {
        const dlg = document.querySelector('.e-filter-popup.e-dialog.e-popup-open');
        if (!dlg) return;

        dlg.style.transform = '';
        const vpH      = window.innerHeight;
        const rect     = dlg.getBoundingClientRect();
        const overflow = Math.round(rect.bottom) - (vpH - MARGIN);

        if (overflow > 0) {
          dlg.style.transform = `translateY(-${overflow}px)`;

          if (dlg.offsetHeight > vpH - MARGIN * 2) {
            const footer  = dlg.querySelector('.e-footer-content');
            const menu    = dlg.querySelector('.e-contextmenu-wrapper');
            const content = dlg.querySelector('.e-dlg-content');
            if (content) {
              const maxH = vpH - MARGIN * 2 - (footer?.offsetHeight || 56) - (menu?.offsetHeight || 0);
              content.style.maxHeight = Math.max(80, maxH) + 'px';
              content.style.overflowY = 'auto';
            }
          }
        }
      }, 100);
    };

    const onClick = (e) => {
      if (e.target.closest('.e-filtermenudiv, .e-excl-filter-icon')) fixFilterDialog();
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // init
  React.useEffect(() => {
    ApiClient.doPost(EndPoint.getFlows, {})
      .then(r => r?.result && setFlows(r.result))
      .catch(console.error);
  }, []);

  // helpers
  const applyPreset = (i, days) => {
    setPreset(i);
    const { from, to } = getRange(days);
    setDateFrom(from); setDateTo(to);
  };

  // Select multiple de flujos: conserva el ORDEN de seleccion para que el
  // primero elegido siga siendo el "principal" (columnas / filtro de vars).
  const onFlowsChange = (e) => {
    const picked = new Set(Array.from(e.target.selectedOptions).map(o => o.value));
    setSelFlows(prev => {
      const ordered = prev.filter(v => picked.has(v));
      picked.forEach(v => { if (!ordered.includes(v)) ordered.push(v); });
      return ordered;
    });
  };

  const fixedCols = React.useMemo(() => getFixedCols(L), [L]);
  // VARNAME -> VARLABEL traducido, para resolver las columnas fijas en
  // render sin depender de lo que haya guardado `header` en el state (que
  // solo se recalcula al buscar). Evita que un cambio de idioma sin volver
  // a buscar deje columnas viejas — probado en vivo: con un useEffect que
  // reescribe `header` quedaba una carrera (el Grid remonta por `key` en el
  // mismo render que trae el header todavia sin traducir).
  const fixedColLabels = React.useMemo(
    () => new Map(fixedCols.map(c => [c.VARNAME, c.VARLABEL])),
    [fixedCols]
  );

  const buildHeader = React.useCallback(async (fid) => {
    let vars = flowVars;
    if (!vars.length) {
      const d = await prepareGetFlowVars({ id: 0 });
      vars = d.result || [];
      setFlowVars(vars);
    }
    setHeader([...fixedCols, ...vars.filter(k => String(k.FLOWID) === String(fid))]);
  }, [flowVars, fixedCols]);

  // Vars del primer flujo elegido (principal), para el select del filtro por
  // variable. flowVars se carga lazy la primera vez que se necesita.
  React.useEffect(() => {
    setVarFilterName('');
    setVarFilterValue('');
    if (!primaryFlow) return;
    if (!flowVars.length) {
      prepareGetFlowVars({ id: 0 })
        .then(d => setFlowVars(d.result || []))
        .catch(console.error);
    }
  }, [primaryFlow]);

  const varOptions = React.useMemo(
    () => flowVars.filter(k => String(k.FLOWID) === String(primaryFlow)),
    [flowVars, primaryFlow]
  );

  // overrides permite disparar la busqueda con valores recien calculados
  // (ej. deep-link desde el widget de errores del header) sin esperar a que
  // el estado de React se re-renderice antes de leerlo.
  const doSearch = async (overrides = {}) => {
    const fromToUse     = overrides.from     ?? dateFrom;
    const toToUse       = overrides.to       ?? dateTo;
    const wfpflowidToUse = overrides.wfpflowid ?? wfpflowidFilter;
    const statusToUse   = 'status' in overrides ? overrides.status : (selStatus !== 'all' ? selStatus : null);
    // Toda busqueda nueva arranca en la pagina 0; solo los botones de
    // paginacion pasan overrides.page explicito.
    const pageToUse     = overrides.page ?? 0;

    // Flujos a buscar: el deep-link puede traer uno puntual (overrides.flowid);
    // la busqueda normal usa todos los seleccionados en el select multiple.
    let flowIds = [];
    if (overrides.flowid) {
      flowIds = Array.isArray(overrides.flowid) ? overrides.flowid.slice() : [String(overrides.flowid)];
    } else {
      flowIds = selFlows.slice();
    }
    // Solo el PRIMER flujo seleccionado define las columnas (variables) y el
    // filtro por variable; los siguientes participan solo de la busqueda.
    const primaryFlowId = flowIds.length ? flowIds[0] : '';

    // Si se busca por ID de instancia exacto no hace falta elegir el proceso
    // (el backend acepta wfpflowid solo); solo se exige un flujo para la
    // busqueda "normal" por rango de fechas/proceso.
    if (!primaryFlowId && !wfpflowidToUse) {
      dispatch(ProcessActions.setMessage({ status: false, message: L.selectProcessReq }));
      return;
    }
    setLoading(true); setSearched(true);
    setPage(pageToUse);
    if (primaryFlowId) await buildHeader(primaryFlowId);
    else setHeader(fixedCols);

    // Invocacion SECUENCIAL de la busqueda: una llamada por flujo seleccionado,
    // sin tocar la API del backend. Resultados concatenados y KPIs acumulados.
    const base = {
      from: fromToUse, to: toToUse, wfpflowid: wfpflowidToUse || null,
      status: statusToUse, search: officeQ || null, page: pageToUse,
      varname: varFilterName || null,
      varvalue: (varFilterName && varFilterValue) ? varFilterValue : null,
    };
    const ids = flowIds.length ? flowIds : [null];

    let allFlows = [], allTasks = [], total = 0, active = 0, completed = 0, error = 0;
    let accTotal = 0, accMinutes = 0, anyOk = false;

    for (const fid of ids) {
      const resp = await prepareGetHistoryFlows({ ...base, flowid: fid });
      if (!resp.status) continue;
      anyOk = true;
      allFlows = allFlows.concat(resp.flows || []);
      allTasks = allTasks.concat(resp.tasks || []);
      total += resp.total || 0;
      active += resp.activeCount || 0;
      completed += resp.completedCount || 0;
      error += resp.errorCount || 0;
      if (resp.total) { accTotal += resp.total; accMinutes += (resp.avgMinutes || 0) * resp.total; }
    }

    if (anyOk) {
      // Promedio de duracion ponderado por la cantidad de instancias de cada flujo.
      const avgMin = accTotal ? accMinutes / accTotal : 0;
      const avg = avgMin <= 0 ? '—' : avgMin < 60 ? `${Math.round(avgMin)}m` : `${Math.floor(avgMin / 60)}h ${Math.round(avgMin % 60)}m`.trim();
      // Orden por ID a nivel de front: el back ya ordena por STARTDATE/ID
      // dentro de un mismo flujo, pero al elegir varios flujos los resultados
      // se concatenan secuencialmente (una llamada por flujo) y quedan
      // intercalados. Se reordena acá para que la vista final sea consistente
      // sin importar cuántos flujos se hayan buscado a la vez.
      allFlows.sort((a, b) => (b.WFPFLOWID || 0) - (a.WFPFLOWID || 0));
      allTasks.sort((a, b) => (a.ID || 0) - (b.ID || 0));
      setHistoryFlows(allFlows);
      setHistoryTasks(allTasks);
      setStats({ total, active, completed, error, avg });
      setPageInfo({ total, pageSize: 50, hasMore: Math.ceil(total / 50) > pageToUse + 1 });
    } else {
      setHistoryFlows([]); setHistoryTasks([]);
      setStats({ total: 0, active: 0, completed: 0, error: 0, avg: '—' });
      setPageInfo({ total: 0, pageSize: 50, hasMore: false });
    }
    setLoading(false);
  };

  // Deep-link: ?wfpflowid=<instancia>&flowid=<proceso>&from=<ISO>&to=<ISO>
  // Usado por el link "Ver en buscador" del widget de errores del header
  // (MainLayout). Depende de location.search (no []) porque el link navega
  // con react-router `navigate()` dentro de la MISMA ruta/pagina -> el
  // componente no se desmonta, asi que un efecto con [] solo dispara la
  // primera vez. location.search SI cambia en cada click (react-router
  // re-renderiza), lo que dispara este efecto de nuevo para cada instancia.
  // Se limpia la URL nativa despues de leerla para que un refresh manual
  // posterior no repita la busqueda.
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qWfp = params.get('wfpflowid');
    const qFlowId = params.get('flowid');
    if (!qWfp && !qFlowId) return;

    const qFrom = params.get('from');
    const qTo = params.get('to');
    const from = qFrom ? new Date(qFrom) : dateFrom;
    const to = qTo ? new Date(qTo) : dateTo;

    if (qFlowId) setSelFlows([qFlowId]);
    if (qWfp) setWfpflowidFilter(qWfp);
    setDateFrom(from);
    setDateTo(to);
    setSelStatus('all');

    window.history.replaceState({}, '', window.location.pathname);
    doSearch({ flowid: qFlowId, wfpflowid: qWfp, from, to, status: null });
  }, [location.search]);

  const handleReactivate = async (task) => {
    if (!task?.ID) return;
    const ok = window.confirm(
      `${L.reactivateTask}: "${task.TASK}"\n\n` +
      (defLang === 'es-es'
        ? 'Volverá a quedar PENDIENTE en la bandeja. No se revierte el avance del flujo.'
        : 'It will be set back to PENDING in the inbox. The flow progress is not reversed.')
    );
    if (!ok) return;
    const resp = await reactivateTask(task.ID);
    dispatch(ProcessActions.setMessage({
      status: !!resp.status,
      message: resp.message_es || resp.message ||
        (resp.status ? L.taskReactivated : L.taskReactivateError),
    }));
    if (resp.status) doSearch({ page });
  };

  const handleKillTask = async (task) => {
    if (!task?.ID) return;
    const ok = window.confirm(
      `${L.killTask}: "${task.TASK}"\n\n` +
      (defLang === 'es-es'
        ? 'Esta tarea quedará finalizada por la fuerza. El resto del flujo NO se revierte ni se detiene.'
        : 'This task will be forcefully finished. The rest of the flow is not reverted or stopped.')
    );
    if (!ok) return;
    const resp = await killTask(task.ID);
    dispatch(ProcessActions.setMessage({
      status: !!resp.status,
      message: resp.message_es || resp.message ||
        (resp.status ? L.taskKilled : L.taskKillError),
    }));
    if (resp.status) doSearch({ page });
  };

  const onToolbar = async (args) => {
    if (args.item.id === 'refresh') {
      doSearch({ page });
    } else if (args.item.id === 'details') {
      const sel = gridRef?.getSelectedRecords();
      if (sel?.length === 1) setDetailRow(sel[0]);
      else dispatch(ProcessActions.setMessage({ status: false, message: L.selectRowDetails }));
    } else if (args.item.id === 'diagram') {
      const sel = gridRef?.getSelectedRecords();
      if (sel?.length === 1) {
        const r = sel[0];
        setDiagProps({ flowId: r.flowid, id: r.WFPFLOWID, version: r.version, tasks: [] });
        setShowStudioView(true);
      } else dispatch(ProcessActions.setMessage({ status: false, message: L.selectRowDiagram }));
    } else if (args.item.id === 'export-csv') {
      exportCSV(historyFlows, header);
    } else if (args.item.id === 'export-xlsx') {
      exportExcel(historyFlows, header);
    }
  };

  const onRowDblClick = (args) => setDetailRow(args.rowData);

  // Ver todas las tareas de un flujo sin paginar — aplica a TODAS las
  // sub-grillas (childGrid es un único config compartido por todas las filas
  // expandidas), no por fila individual. Se sube pageSize en vez de tocar
  // allowPaging: alternar allowPaging rompe el query binding (queryString)
  // del child grid ya renderizado y la sub-grilla queda vacía ("No hay
  // registros que mostrar") hasta volver a expandir — bug verificado en vivo.
  const [showAllTasks, setShowAllTasks] = React.useState(false);

  const childGrid = React.useMemo(() => ({
    dataSource: historyTasks,
    queryString: 'WFPFLOWID',
    allowPaging: true,
    pageSettings: { pageSize: showAllTasks ? 10000 : 6, pageCount: 5 },
    rowHeight: 34,
    columns: [
      { field: 'TASK',          headerText: L.colTask,          width: 140, clipMode: 'EllipsisWithTooltip' },
      { field: 'STATUSID',      headerText: L.taskStatus,      width: 110, template: TaskStatusTemplate },
      { field: 'TRYOUT',        headerText: L.taskAttempts,    width: 80,  textAlign: 'Center', template: TaskAttemptsTemplate },
      // type: 'string' overrides Syncfusion's auto-detected 'date' type (raw values are
      // Date instances) — without it, Grid re-parses valueAccessor's formatted string as
      // a date internally and fails, rendering "Invalid Date".
      { field: 'STARTDATE',     headerText: L.colStarted,       width: 170, clipMode: 'EllipsisWithTooltip', type: 'string', valueAccessor: dateAwareValueAccessor },
      { field: 'ENDDATE',       headerText: L.colEnded,         width: 170, clipMode: 'EllipsisWithTooltip', type: 'string', valueAccessor: dateAwareValueAccessor },
      { field: 'USERNAME',      headerText: L.colUser,          width: 120, clipMode: 'EllipsisWithTooltip' },
      { field: 'ACTOR',         headerText: L.colActor,         width: 120, clipMode: 'EllipsisWithTooltip' },
      { field: 'OFFICE',        headerText: L.summaryOffice,    width: 140, clipMode: 'EllipsisWithTooltip' },
      { field: 'ADDCOMMENTARY', headerText: L.response,         width: 260, template: ResponseTemplate },
    ],
  }), [historyTasks, L, showAllTasks]);

  // ─── tema reactivo ────────────────────────────────────────────────────────

  const isDark = useDarkMode();

  React.useEffect(() => {
    gridRef?.refresh();
  }, [isDark]);

  const T = isDark ? {
    pageBg:        '#0b1220',
    panelBg:       '#111a2e',
    border:        '#1e2d47',
    textPrimary:   '#c8d8f0',
    textSecondary: '#7a9bbf',
    textLabel:     '#4a6180',
    blue:          '#38a4d8',
    blueLt:        'rgba(56,164,216,0.12)',
    cardShadow:    '0 1px 2px rgba(0,0,0,0.24), 0 8px 24px -12px rgba(0,0,0,0.45)',
    ctrlH: 32,
  } : {
    pageBg:        '#f5f6f7',
    panelBg:       '#ffffff',
    border:        '#e0e0e0',
    textPrimary:   '#1d2129',
    textSecondary: '#6b7280',
    textLabel:     '#8a8d91',
    blue:          '#0070f2',
    blueLt:        '#e8f1fd',
    cardShadow:    '0 1px 2px rgba(16,24,40,0.04), 0 4px 16px -8px rgba(16,24,40,0.10)',
    ctrlH: 32,
  };

  const cardRadius = 8;

  const filterLabel = {
    display: 'block', fontSize: 11, fontWeight: 600, color: T.textLabel,
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4,
  };

  const ctrl = {
    height: T.ctrlH, boxSizing: 'border-box',
    border: `1px solid ${T.border}`, borderRadius: 6,
    fontSize: 13, color: T.textPrimary, background: isDark ? '#1a2540' : '#fff',
    padding: '0 10px', outline: 'none', width: '100%',
    colorScheme: isDark ? 'dark' : 'light',
  };

  const btnBase = {
    height: T.ctrlH, borderRadius: 6, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex',
    alignItems: 'center', gap: 5, padding: '0 14px', border: '1px solid',
  };

  const btnPrimary = { ...btnBase, background: T.blue, color: '#fff', borderColor: T.blue };
  const btnGhost   = { ...btnBase, background: isDark ? '#1e2d47' : '#fff', color: T.textSecondary, borderColor: T.border };

  // ─── toolbar ─────────────────────────────────────────────────────────────

  const mobileToolbarOpts = React.useMemo(() => [
    { text: L.refresh,     id: 'refresh',      tooltipText: L.refresh,      prefixIcon: 'e-icons e-refresh'       },
    { text: L.viewData,    id: 'details',      tooltipText: L.viewData,     prefixIcon: 'e-icons e-description'   },
    { text: L.diagram,     id: 'diagram',      tooltipText: L.diagram,      prefixIcon: 'e-icons e-diagram'       },
    { text: 'CSV',      id: 'export-csv', tooltipText: 'CSV',           prefixIcon: 'e-icons e-download'       },
    { text: 'Excel',    id: 'export-xlsx',tooltipText: 'Excel',         prefixIcon: 'e-icons e-export-excel'   },
    'Search',
  ], [L]);

  const toolbarOpts = React.useMemo(() => {
    const s = [
      'display:inline-flex', 'align-items:center', 'gap:5px',
      'height:28px', 'padding:0 11px', 'border-radius:4px',
      'font-size:12px', 'font-weight:600', 'cursor:pointer',
      `border:1px solid ${T.border}`,
      `background:${isDark ? '#1e2d47' : '#fff'}`,
      `color:${T.textPrimary}`,
      'white-space:nowrap', 'line-height:1', 'outline:none',
      'font-family:inherit',
    ].join(';');

    const ico = (d) => `<svg class="swf-tb-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
    const ICO = {
      refresh:      ico('<polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>'),
      eye:          ico('<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>'),
      // Diagrama: visor FlowStudio (lanes/colores). El icono viejo (Syncfusion
      // legacy, círculos+líneas) se retiró junto con el botón/dialog.
      diagram:      ico('<path d="M12 2 2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>'),
      'export-csv': ico('<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>'),
      'export-xlsx':ico('<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/><path d="m6.5 13.5 2 3 2-3M14 13h3M14 16.5h3"/>'),
    };
    const btn = (icon, label, id, title) =>
      `<button class="swf-tb-btn" style="${s}" id="${id}" title="${title || label}" type="button">${ICO[icon]}<span>${label}</span></button>`;

    return [
      { template: btn('refresh',     L.refresh,  'refresh',     L.refresh),    id: 'refresh',     tooltipText: L.refresh    },
      { template: btn('eye',         L.viewData, 'details',     L.viewData),   id: 'details',     tooltipText: L.viewData   },
      { template: btn('diagram',     L.diagram,  'diagram',     L.diagram),    id: 'diagram',     tooltipText: L.diagram    },
      { template: btn('export-csv',  'CSV',      'export-csv',  'CSV'),        id: 'export-csv',  tooltipText: 'CSV'        },
      { template: btn('export-xlsx', 'Excel',    'export-xlsx', 'Excel'),      id: 'export-xlsx', tooltipText: 'Excel'      },
      'Search',
    ];
  }, [isDark, L]);

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ background: T.pageBg, minHeight: '100%', padding: '12px 12px 24px', fontFamily: 'inherit' }}>

      {loading && <Loading timer={4000} contained={true} />}

      {detailRow && (
        <FlowDetailPanel
          row={detailRow}
          tasks={historyTasks}
          header={header}
          onClose={() => setDetailRow(null)}
          isDark={isDark}
          isAdmin={isAdminUser()}
          onReactivate={isAdminUser() ? handleReactivate : null}
          onKill={isAdminUser() ? handleKillTask : null}
          L={L}
        />
      )}

      {showStudioView && (
        <React.Suspense fallback={null}>
          <FlowStudioViewer
            open={showStudioView}
            close={() => setShowStudioView(false)}
            flowId={diagProps.flowId}
            id={diagProps.id}
            version={diagProps.version}
          />
        </React.Suspense>
      )}

      {/* Cell detail dialog */}
      {detail !== null && (
        <DialogComponent header={L.responseDetail} width="660px"
          cssClass="swf-response-dlg"
          isModal={true} showCloseIcon={true} visible={true}
          close={() => setDetail(null)} overlayClick={() => setDetail(null)}>
          <div style={{ maxHeight: 480, overflowY: 'auto', padding: '2px 4px' }}>
            {parseResponse(detail).map((sec, i, arr) => (
              <div key={i} style={{ marginBottom: arr.length > 1 ? 16 : 0 }}>
                {arr.length > 1 && (
                  <p style={{ fontSize: 11, fontWeight: 600, color: T.textLabel, textTransform: 'uppercase', marginBottom: 6 }}>
                    {L.response} {i + 1}
                  </p>
                )}
                {sec.type === 'json'
                  ? <JsonViewer data={sec.data} />
                  : <p className="swf-raw-text" style={{ background: T.panelBg === '#ffffff' ? '#f6f8fa' : T.panelBg, border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 14px', margin: 0, fontSize: 13, color: T.textPrimary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{sec.raw || '—'}</p>
                }
              </div>
            ))}
          </div>
        </DialogComponent>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          FILTER PANEL
      ════════════════════════════════════════════════════════════════════ */}
      <div style={{ background: T.panelBg, border: `1px solid ${T.border}`, borderRadius: cardRadius, boxShadow: T.cardShadow, marginBottom: 12 }}>

        {/* Panel header */}
        <div style={{
          padding: '12px 18px', borderBottom: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, letterSpacing: '-0.1px' }}>
            {L.title}
          </span>
          <span style={{ fontSize: 12, color: T.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
            {fmtShort(dateFrom)} – {fmtShort(dateTo)}
          </span>
        </div>

        {/* Filter body */}
        <div style={{ padding: '16px 18px 18px' }}>

          {/* Row 1 — date period */}
          <div style={{ marginBottom: 14 }}>
            <label style={filterLabel}>{L.datePeriod}</label>
            <div className="swf-date-row">
              <div className="swf-presets">
                {DATE_PRESETS_BASE.map((p, i) => (
                  <button key={i} onClick={() => applyPreset(i, p.days)} style={{
                    ...btnBase,
                    height: T.ctrlH, padding: '0 12px', fontSize: 12,
                    background:   preset === i ? T.blueLt : (isDark ? '#1a2540' : '#f5f6f7'),
                    color:        preset === i ? T.blue   : T.textSecondary,
                    borderColor:  preset === i ? T.blue   : T.border,
                    fontWeight:   preset === i ? 600      : 400,
                  }}>
                    {p.labelKey ? L[p.labelKey] : `${p.days} ${L.daysUnit}`}
                  </button>
                ))}
              </div>
              <div className="swf-daterange-wrap">
                <DateRangePickerComponent
                  startDate={dateFrom} endDate={dateTo}
                  format="dd/MMM/yy"
                  onChange={e => { if (e.value?.[0] && e.value?.[1]) { setDateFrom(e.value[0]); setDateTo(e.value[1]); setPreset(-1); } }}
                  placeholder={L.customRange}
                  cssClass="e-outline"
                />
              </div>
            </div>
          </div>

          {/* Row 2 — filters grid (4 equal cols + action col) */}
          <div className="swf-filters">

            {/* Process */}
            <div>
              <label style={filterLabel}>{L.processLabel}</label>
              <select
                multiple
                value={selFlows}
                onChange={onFlowsChange}
                title={L.multiFlowHint}
                style={{ ...ctrl, height: 'auto', minHeight: ctrl.height, maxHeight: 168 }}
              >
                {flows.map(f => <option key={f.FLOWID} value={f.FLOWID}>{f.FLOW}</option>)}
              </select>
              {selFlows.length > 1 && (
                <div style={{ fontSize: 11, marginTop: 4, color: T.textSecondary }}>
                  {selFlows.length} {L.multiFlowCount} · {L.multiFlowPrimaryHint}
                </div>
              )}
            </div>

            {/* Status */}
            <div>
              <label style={filterLabel}>{L.statusLabel}</label>
              <select value={selStatus} onChange={e => setSelStatus(e.target.value)} style={ctrl}>
                <option value="all">{L.allStatuses}</option>
                <option value="active">{L.swfInProgress}</option>
                <option value="completed">{L.swfCompleted}</option>
                <option value="error">{L.swfError}</option>
              </select>
            </div>

            {/* Office */}
            <div>
              <label style={filterLabel}>{L.officeLabel}</label>
              <input
                value={officeQ}
                readOnly={noFill}
                onFocus={() => setNoFill(false)}
                onChange={e => setOfficeQ(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && doSearch()}
                placeholder={L.officePH}
                style={ctrl}
              />
            </div>

            {/* Instancia exacta (WFP_TMP_FLOWS.ID) — mismo campo que usa el deep-link */}
            <div>
              <label style={filterLabel}>{L.instanceIdLabel || 'ID Instancia'}</label>
              <input
                value={wfpflowidFilter || ''}
                onChange={e => setWfpflowidFilter(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && doSearch()}
                placeholder={L.instanceIdPH || 'Ej: 263'}
                style={ctrl}
              />
            </div>

            {/* Variable de instancia (solo si el proceso elegido tiene vars) */}
            {varOptions.length > 0 && (
              <div className="swf-filter-variable">
                <label style={filterLabel}>{L.varFilterLabel || 'Variable'}</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select
                    value={varFilterName}
                    onChange={e => setVarFilterName(e.target.value)}
                    style={{ ...ctrl, width: '50%' }}
                  >
                    <option value="">{L.varFilterAny || '— Variable —'}</option>
                    {varOptions.map(v => (
                      <option key={v.VARNAME} value={v.VARNAME}>{v.VARLABEL || v.VARNAME}</option>
                    ))}
                  </select>
                  <input
                    value={varFilterValue}
                    disabled={!varFilterName}
                    onChange={e => setVarFilterValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && doSearch()}
                    placeholder={L.varFilterValuePH || 'Valor contiene...'}
                    style={{ ...ctrl, width: '50%', opacity: varFilterName ? 1 : 0.5 }}
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => doSearch()} style={btnPrimary}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                {L.searchBtn}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          RESULTS SECTION
      ════════════════════════════════════════════════════════════════════ */}
      {searched && (
        <div style={{ background: T.panelBg, border: `1px solid ${T.border}`, borderRadius: cardRadius, boxShadow: T.cardShadow, overflow: 'hidden' }}>

          {/* Results header bar — dos filas: (1) título + paginación,
              (2) KPIs + toggle de tareas, todo en chips compactos horizontales
              en vez de barras verticales de ancho completo. */}
          <div style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${T.border}`,
            display: 'flex', flexDirection: 'column', gap: 10,
            background: T.panelBg,
          }}>
            {/* Fila 1 — título + paginación */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary }}>
                {L.resultsTitle}
                <span style={{ fontSize: 12, fontWeight: 400, color: T.textSecondary, marginLeft: 6 }}>
                  ({stats.total})
                </span>
              </span>

              {/* Paginacion server-side (paginas de 50) */}
              {pageInfo.total > pageInfo.pageSize && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <button
                    onClick={() => doSearch({ page: page - 1 })}
                    disabled={page === 0 || loading}
                    style={{ ...btnGhost, opacity: page === 0 ? 0.5 : 1, cursor: page === 0 ? 'default' : 'pointer' }}
                  >
                    ←
                  </button>
                  <span style={{ fontSize: 12, color: T.textSecondary, whiteSpace: 'nowrap' }}>
                    {(L.pageLabel || 'Página')} {page + 1} / {Math.max(1, Math.ceil(pageInfo.total / pageInfo.pageSize))}
                  </span>
                  <button
                    onClick={() => doSearch({ page: page + 1 })}
                    disabled={!pageInfo.hasMore || loading}
                    style={{ ...btnGhost, opacity: !pageInfo.hasMore ? 0.5 : 1, cursor: !pageInfo.hasMore ? 'default' : 'pointer' }}
                  >
                    →
                  </button>
                </div>
              )}
            </div>

            {/* Fila 2 — KPIs (chips compactos, no barras full-width) + toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <KpiChip label={L.swfInProgress} value={stats.active}    color={STATUS_CFG.active.dot}    bg={STATUS_CFG.active.bg}    fg={STATUS_CFG.active.fg}    dark={isDark} />
              <KpiChip label={L.swfCompleted}  value={stats.completed} color={STATUS_CFG.completed.dot} bg={STATUS_CFG.completed.bg} fg={STATUS_CFG.completed.fg} dark={isDark} />
              <KpiChip label={L.swfError}      value={stats.error}     color={STATUS_CFG.error.dot}     bg={STATUS_CFG.error.bg}     fg={STATUS_CFG.error.fg}     dark={isDark} />
              <KpiChip label={L.avgDuration}   value={stats.avg}       color="#8e44ad" bg="#f5edfb" fg="#6c3483"                                                   dark={isDark} />

              {!isMobile && (
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  marginLeft: 'auto', paddingLeft: 10,
                  borderLeft: `1px solid ${T.border}`,
                  fontSize: 12, color: T.textSecondary, cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                  <input
                    type="checkbox"
                    checked={showAllTasks}
                    onChange={e => setShowAllTasks(e.target.checked)}
                    style={{ margin: 0, cursor: 'pointer' }}
                  />
                  {L.showAllTasks}
                </label>
              )}
            </div>
          </div>

          {/* Grid */}
          {historyFlows.length > 0 ? (
            isMobile ? (
              <GridComponent id="swf-m" key={`swf-m-${defLang}`} ref={g => { gridRef = g; }}
                cssClass="swf-grid"
                toolbarClick={onToolbar} toolbar={mobileToolbarOpts}
                dataSource={historyFlows} rowHeight={38}
                enableAdaptiveUI={true} rowRenderingMode="Vertical"
                allowFiltering={true} allowSorting={true} allowPaging={true}
                filterSettings={{ type: 'Menu' }}
                pageSettings={{ pageCount: 3, pageSize: 5, pageSizes: true }}
                recordDoubleClick={onRowDblClick}>
                <ColumnsDirective>
                  <ColumnDirective headerText="" width="60" allowFiltering={false} allowSorting={false} template={DetailButtonTemplate} />
                  {header.map((h, i) => {
                    if (h.VARNAME === 'flowStatus')
                      return <ColumnDirective key={h.VARNAME} field={h.VARNAME} headerText={L.statusLabel} width="130" template={StatusTemplate} />;
                    if (h.VARNAME === 'durationLabel')
                      return <ColumnDirective key={h.VARNAME} field={h.VARNAME} headerText={L.summaryDuration} width="90"  template={DurationTemplate} />;
                    // startDate/endDate hold real Date objects — force type="string" so Syncfusion
                    // doesn't re-parse the already-formatted valueAccessor output as a date (→ "Invalid Date").
                    if (h.VARNAME === 'startDate' || h.VARNAME === 'endDate')
                      return <ColumnDirective key={h.VARNAME} field={h.VARNAME} headerText={fixedColLabels.get(h.VARNAME) || h.VARLABEL} width="170" clipMode="EllipsisWithTooltip" type="string" valueAccessor={dateAwareValueAccessor} />;
                    return <ColumnDirective key={h.VARNAME} field={h.VARNAME} headerText={fixedColLabels.get(h.VARNAME) || h.VARLABEL} width={i === 0 ? '100' : '170'} clipMode="EllipsisWithTooltip" valueAccessor={dateAwareValueAccessor} />;
                  })}
                </ColumnsDirective>
                <Inject services={[Filter, Sort, Toolbar, Page]} />
              </GridComponent>
            ) : (
              <GridComponent id="swf-d" key={`swf-d-${defLang}`} ref={g => { gridRef = g; }}
                cssClass="swf-grid"
                allowTextWrap={false} childGrid={childGrid}
                filterSettings={{ type: 'Excel' }}
                toolbarClick={onToolbar} toolbar={toolbarOpts}
                dataSource={historyFlows} rowHeight={34}
                allowFiltering={true} allowSorting={true} allowPaging={true}
                pageSettings={{ pageCount: 3, pageSize: 10, pageSizes: true }}
                recordDoubleClick={onRowDblClick}>
                <ColumnsDirective>
                  <ColumnDirective headerText="" width="60" allowFiltering={false} allowSorting={false} template={DetailButtonTemplate} />
                  {header.map((h, i) => {
                    if (h.VARNAME === 'flowStatus')
                      return <ColumnDirective key={h.VARNAME} field={h.VARNAME} headerText={L.statusLabel} width="130" template={StatusTemplate} />;
                    if (h.VARNAME === 'durationLabel')
                      return <ColumnDirective key={h.VARNAME} field={h.VARNAME} headerText={L.summaryDuration} width="90"  template={DurationTemplate} />;
                    // startDate/endDate hold real Date objects — force type="string" so Syncfusion
                    // doesn't re-parse the already-formatted valueAccessor output as a date (→ "Invalid Date").
                    if (h.VARNAME === 'startDate' || h.VARNAME === 'endDate')
                      return <ColumnDirective key={h.VARNAME} field={h.VARNAME} headerText={fixedColLabels.get(h.VARNAME) || h.VARLABEL} width="170" clipMode="EllipsisWithTooltip" type="string" valueAccessor={dateAwareValueAccessor} />;
                    return <ColumnDirective key={h.VARNAME} field={h.VARNAME} headerText={fixedColLabels.get(h.VARNAME) || h.VARLABEL} width={i === 0 ? '100' : '170'} clipMode="EllipsisWithTooltip" valueAccessor={dateAwareValueAccessor} />;
                  })}
                </ColumnsDirective>
                <Inject services={[Filter, Sort, DetailRow, Toolbar, Page]} />
              </GridComponent>
            )
          ) : (
            <div style={{ textAlign: 'center', padding: '56px 20px', color: T.textSecondary }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.border} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: T.textPrimary }}>{L.noInstances}</p>
              <p style={{ margin: 0, fontSize: 13 }}>{L.noInstancesHint}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── KPI chip (small inline summary) ─────────────────────────────────────────

// Chip compacto (ancho automatico, no una barra que ocupa todo el
// contenedor) — antes tenia el label con flex:1 dentro de un padre flex:1,
// lo que estiraba cada KPI a lo ancho de la fila y los apilaba como barras.
function KpiChip({ label, value, color, bg, fg, dark }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: dark ? `${color}1f` : bg,
      border: `1px solid ${color}33`,
      borderRadius: 5, padding: '4px 10px',
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <span style={{ color: dark ? '#7a9bbf' : '#6b7280', fontSize: 12 }}>{label}</span>
      <span style={{ fontWeight: 700, color: dark ? color : fg, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </span>
  );
}

export default SearchWF;
